import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { z } from 'zod';

import { ApiError } from '../api/client.js';
import { authApi } from '../api/endpoints.js';
import { useSession } from '../auth/session.js';
import { AuthLayout } from '../components/auth-layout.js';
import { BrandMark } from '../components/brand.js';
import { Alert, Button, Field } from '../components/ui.js';

const signInSchema = z.object({
  email: z.string().min(1, 'Enter your email address.').email('That does not look like an email.'),
  password: z.string().min(1, 'Enter your password.'),
});

type SignInValues = z.infer<typeof signInSchema>;

const forgotSchema = z.object({
  email: z.string().min(1, 'Enter your email address.').email('That does not look like an email.'),
});

type ForgotValues = z.infer<typeof forgotSchema>;

type View = 'sign-in' | 'forgot';

export function SignInPage(): ReactNode {
  const { user, isLoading } = useSession();
  const [view, setView] = useState<View>('sign-in');

  if (isLoading) return <AuthLayout>{null}</AuthLayout>;
  if (user !== null) return <Navigate to="/" replace />;

  return (
    <AuthLayout>
      {view === 'sign-in' ? (
        <SignInCard
          onForgot={() => {
            setView('forgot');
          }}
        />
      ) : (
        <ForgotCard
          onBack={() => {
            setView('sign-in');
          }}
        />
      )}
    </AuthLayout>
  );
}

function noticeFrom(state: unknown): string | null {
  if (typeof state !== 'object' || state === null || !('notice' in state)) return null;
  const notice = state.notice;
  return typeof notice === 'string' && notice.length > 0 ? notice : null;
}

function SignInCard({ onForgot }: { readonly onForgot: () => void }): ReactNode {
  const { signIn } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [failure, setFailure] = useState<string | null>(null);
  const notice = noticeFrom(location.state);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<SignInValues>({ resolver: zodResolver(signInSchema) });

  const onSubmit = handleSubmit(async (values) => {
    setFailure(null);

    try {
      await signIn(values.email, values.password);
      void navigate('/', { replace: true });
    } catch (error) {
      setFailure(
        error instanceof ApiError ? error.message : 'We could not reach the server. Try again.',
      );
    }
  });

  return (
    <>
      <BrandMark className="mb-6" />
      <h2 className="text-[28px] font-bold tracking-[-0.03em] text-navy">Sign in</h2>
      <p className="mt-1.5 mb-6 text-[14px] leading-snug text-muted">
        Use your Silver Bell Group account to open Task Management.
      </p>

      {notice === null ? null : <Alert tone="success">{notice}</Alert>}
      {failure === null ? null : <Alert tone="error">{failure}</Alert>}

      <form
        onSubmit={(event) => {
          void onSubmit(event);
        }}
        noValidate
      >
        <Field
          label="Email"
          type="email"
          autoComplete="username"
          autoFocus
          error={errors.email?.message}
          {...register('email')}
        />
        <Field
          label="Password"
          type="password"
          autoComplete="current-password"
          error={errors.password?.message}
          {...register('password')}
        />

        <div className="-mt-1 mb-4 flex justify-end">
          <button
            type="button"
            onClick={onForgot}
            className="text-[13px] font-semibold text-royal hover:text-navy hover:underline"
          >
            Forgot password?
          </button>
        </div>

        <Button type="submit" size="lg" loading={isSubmitting} className="w-full">
          Continue
        </Button>
      </form>

      <p className="mt-5 text-center text-[12px] tracking-[0.04em] text-muted">
        Silver Bell Group · Task Management
      </p>
    </>
  );
}

function ForgotCard({ onBack }: { readonly onBack: () => void }): ReactNode {
  const [sent, setSent] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotValues>({ resolver: zodResolver(forgotSchema) });

  const onSubmit = handleSubmit(async (values) => {
    try {
      await authApi.forgotPassword(values.email);
    } finally {
      setSent(true);
    }
  });

  return (
    <>
      <BrandMark className="mb-6" />
      <h2 className="text-[28px] font-bold tracking-[-0.03em] text-navy">Forgot password?</h2>
      <p className="mt-1.5 mb-6 text-[14px] leading-snug text-muted">
        Enter your email address. We will send reset instructions if an account matches.
      </p>

      {sent ? (
        <Alert tone="success">
          If that address belongs to an account, a reset link is on its way. The link works once and
          expires in 30 minutes.
        </Alert>
      ) : null}

      <form
        onSubmit={(event) => {
          void onSubmit(event);
        }}
        noValidate
      >
        <Field
          label="Email"
          type="email"
          autoComplete="username"
          error={errors.email?.message}
          {...register('email')}
        />

        <Button type="submit" size="lg" loading={isSubmitting} className="w-full">
          Send reset link
        </Button>
      </form>

      <Button variant="secondary" size="lg" onClick={onBack} className="mt-3 w-full">
        Back to sign in
      </Button>

      <p className="mt-5 text-center text-[12px] tracking-[0.04em] text-muted">
        Silver Bell Group · Task Management
      </p>
    </>
  );
}
